<template>
  <div class="tongue-report-page">
    <div class="phone-shell">
      <main class="page-body">
        <div v-if="loadError" class="state-card error-card">{{ loadError }}</div>

        <section class="report-section">
          <SectionTitle :index="1" title="基本信息" />
          <div class="card info-card">
            <div v-for="item in basicInfo" :key="item.key" class="info-row">
              <span class="info-icon" :class="[{ 'has-image': item.iconSrc }, item.color]">
                <img v-if="item.iconSrc" :src="item.iconSrc" :alt="item.label" />
                <template v-else>{{ item.icon }}</template>
              </span>
              <span class="info-label">{{ item.label }}</span>
              <span class="info-value">{{ item.value }}</span>
            </div>
          </div>

          <div class="card tinted-card summary-card">
            <div class="card-title purple-title">
              <img class="title-icon" :src="titleIcons.summary" alt="核心摘要" />
              核心摘要
            </div>
            <p>{{ summaryText }}</p>
          </div>
        </section>

        <section class="report-section">
          <SectionTitle :index="2" title="舌象结果" />
          <div class="card tongue-card">
            <div class="tongue-map">
              <div class="tongue-label label-root">
                <span>{{ tongueAnnotations.root.label }}</span>
                <strong>{{ tongueAnnotations.root.value }}</strong>
              </div>
              <div class="tongue-label label-side">
                <span>{{ tongueAnnotations.side.label }}</span>
                <strong>{{ tongueAnnotations.side.value }}</strong>
              </div>
              <div class="tongue-label label-body right">
                <span>{{ tongueAnnotations.body.label }}</span>
                <strong class="red-text">{{ tongueAnnotations.body.value }}</strong>
              </div>
              <div class="tongue-label label-tip right">
                <span>{{ tongueAnnotations.tip.label }}</span>
                <strong>{{ tongueAnnotations.tip.value }}</strong>
              </div>

              <svg class="leader-lines" viewBox="0 0 638 430" aria-hidden="true">
                <path d="M108 132 L288 132" />
                <path d="M108 260 L264 252" />
                <path d="M506 196 L398 244" />
                <path d="M506 343 L324 330" />
              </svg>

              <div class="tongue-figure">
                <svg class="tongue-art" viewBox="0 0 214 304" aria-hidden="true">
                  <defs>
                    <linearGradient id="tongueBodyGradient" x1="38" y1="18" x2="174" y2="286" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stop-color="#ffcfc7" />
                      <stop offset="0.5" stop-color="#ff9d95" />
                      <stop offset="1" stop-color="#ef777a" />
                    </linearGradient>
                    <radialGradient id="tongueHighlight" cx="78" cy="46" r="126" gradientUnits="userSpaceOnUse">
                      <stop offset="0" stop-color="#fff0ec" stop-opacity="0.95" />
                      <stop offset="0.44" stop-color="#ffd1cb" stop-opacity="0.52" />
                      <stop offset="1" stop-color="#ff9b95" stop-opacity="0" />
                    </radialGradient>
                  </defs>
                  <path class="tongue-base" d="M107 10C55 10 28 49 27 112c-1 49 10 95 30 132 17 32 35 48 50 48s33-16 50-48c20-37 31-83 30-132-1-63-28-102-80-102Z" />
                  <path class="tongue-highlight" d="M107 18C62 18 38 53 38 112c0 45 10 88 28 121 14 27 29 41 41 41s27-14 41-41c18-33 28-76 28-121 0-59-24-94-69-94Z" />
                  <path class="tongue-inner" d="M107 26C69 26 47 58 47 113c0 41 9 79 24 110 13 24 26 36 36 36s23-12 36-36c15-31 24-69 24-110 0-55-22-87-60-87Z" />
                  <path class="tongue-midline" d="M107 42C101 91 101 156 107 231" />
                  <path class="tongue-texture left" d="M74 72C66 107 68 159 80 210" />
                  <path class="tongue-texture right" d="M140 76C148 112 146 162 134 214" />
                </svg>
                <i class="point point-root"></i>
                <i class="point point-side"></i>
                <i class="point point-body"></i>
                <i class="point point-tip"></i>
              </div>
            </div>

            <div class="result-list">
              <div v-for="item in tongueResults" :key="item.key" class="result-row">
                <div class="result-meta">
                  <span>{{ item.label }}：{{ item.value }}</span>
                </div>
                <div class="progress-track">
                  <i :class="item.color" :style="{ width: item.percent + '%' }"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="card tinted-card simple-card">
            <div class="card-title blue-title">
              <img class="title-icon" :src="titleIcons.simple" alt="简要理解" />
              简要理解
            </div>
            <p>{{ simpleExplanation }}</p>
          </div>
        </section>

        <section class="report-section">
          <SectionTitle :index="3" title="综合判断" />
          <div class="card problem-card">
            <div>
              <div class="card-title orange-title">
                <img class="title-icon" :src="titleIcons.problem" alt="可能问题" />
                可能问题
              </div>
              <p>{{ possibleProblem }}</p>
            </div>
          </div>

          <div class="card evidence-card">
            <div>
              <div class="card-title green-title">
                <img class="title-icon" :src="titleIcons.evidence" alt="判断依据" />
                判断依据
              </div>
              <div v-for="item in evidenceList" :key="item" class="evidence-row">
                <p>{{ item }}</p>
              </div>
            </div>
            <div class="clipboard-mark"></div>
          </div>

          <div class="card status-card">
            <div class="card-title blue-title">
              <img class="title-icon" :src="titleIcons.status" alt="当前状态" />
              当前状态
            </div>
            <p>{{ currentStatusText }}</p>
            <div class="status-scale">
              <span :style="riskMarkerStyle"></span>
            </div>
            <div class="scale-text">
              <b>轻</b>
              <b>中</b>
              <b>重</b>
            </div>
          </div>
        </section>

        <section class="report-section">
          <SectionTitle :index="4" title="严重程度与核心问题" />
          <div class="card severity-card">
            <div class="risk-panel">
              <div class="card-title blue-title">
                <img class="title-icon" :src="titleIcons.risk" alt="风险等级" />
                风险等级
              </div>
              <div class="gauge-wrap">
                <div class="gauge-arc"></div>
                <div class="gauge-needle" :style="riskNeedleStyle"></div>
              </div>
              <strong>{{ riskText }}</strong>
              <span>{{ riskSubText }}</span>
            </div>

            <div class="core-panel">
              <div class="card-title red-title">
                <img class="title-icon" :src="titleIcons.core" alt="核心问题总结" />
                核心问题总结
              </div>
              <p>{{ coreProblemSummary }}</p>
              <div class="chain-row">
                <div v-for="(item, index) in problemChain" :key="item.label" class="chain-item">
                  <b>{{ item.label }}</b>
                  <i v-if="index < problemChain.length - 1"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="card attention-card">
            <div class="card-title warning-title">
              <img class="title-icon" :src="titleIcons.attention" alt="需要关注" />
              需要关注
            </div>
            <p>{{ attentionText }}</p>
          </div>
        </section>

        <section class="report-section">
          <SectionTitle :index="5" title="调理建议" />
          <div class="card advice-card">
            <div class="card-title green-title">
              <img class="title-icon" :src="titleIcons.diet" alt="饮食方面" />
              饮食方面
            </div>
            <div class="diet-tag-group avoid">
              <div class="advice-label">避免</div>
              <div class="diet-tags">
                <span v-for="item in avoidFoods" :key="item.name">{{ item.name }}</span>
              </div>
            </div>
            <div class="diet-tag-group recommend">
              <div class="advice-label">建议食用</div>
              <div class="diet-tags">
                <span v-for="item in recommendFoods" :key="item.name">{{ item.name }}</span>
              </div>
            </div>
            <div class="meal-time">
              <span>◷</span>
              {{ dietTip }}
            </div>
          </div>

          <div class="card lifestyle-card">
            <div class="card-title purple-title">
              <img class="title-icon" :src="titleIcons.lifestyle" alt="生活方面" />
              生活方面
            </div>
            <div v-for="item in lifestyle" :key="item" class="life-row">
              <p>{{ item }}</p>
            </div>
          </div>
        </section>

      </main>

      <div v-if="isLoading" class="page-loading-mask" role="status" aria-live="polite">
        <span class="loading-spinner"></span>
        <p class="loading-text">报告加载中...</p>
      </div>
    </div>
  </div>
</template>

<script>
const SectionTitle = {
  functional: true,
  props: {
    index: Number,
    title: String
  },
  render(h, { props }) {
    return h('div', { class: 'section-title' }, [
      h('span', { class: 'section-number' }, props.index),
      h('h2', props.title)
    ]);
  }
};

const TONGUE_API_BASE = process.env.VUE_APP_TONGUE_API_BASE || 'https://apihtest.nb-s.cn';
const TONGUE_API_KEY = process.env.VUE_APP_TONGUE_API_KEY || 'f5337b3934616e37fc39b688cb254abbaf916fa9e678e0f1717e91948651e38e';
const TONGUE_ORDER_API_KEY = process.env.VUE_APP_TONGUE_ORDER_API_KEY || 'local-order-api-key';
const DEFAULT_REPORT_ID = process.env.VUE_APP_TONGUE_DEFAULT_REPORT_ID || 'TD202605130724048979205704';

const genderTextMap = {
  male: '男',
  female: '女',
  other: '其他',
  1: '男',
  2: '女'
};

const basicInfoIcons = {
  gender: require('@/assets/tongue-icons/1.png'),
  age: require('@/assets/tongue-icons/2.png'),
  chief_complaint: require('@/assets/tongue-icons/3.png'),
  recent_status: require('@/assets/tongue-icons/4.png')
};

const titleIcons = {
  summary: require('@/assets/tongue-icons/5.png'),
  simple: require('@/assets/tongue-icons/6.png'),
  problem: require('@/assets/tongue-icons/7.png'),
  evidence: require('@/assets/tongue-icons/8.png'),
  status: require('@/assets/tongue-icons/9.png'),
  risk: require('@/assets/tongue-icons/10.png'),
  attention: require('@/assets/tongue-icons/11.png'),
  diet: require('@/assets/tongue-icons/12.png'),
  lifestyle: require('@/assets/tongue-icons/13.png'),
  core: require('@/assets/tongue-icons/14.png')
};

const fallbackReport = {
  basicInfo: [
    { key: 'gender', label: '性别', value: '男', icon: '性', color: 'blue', iconSrc: basicInfoIcons.gender },
    { key: 'age', label: '年龄', value: '35岁', icon: '龄', color: 'blue', iconSrc: basicInfoIcons.age },
    { key: 'chief_complaint', label: '主诉', value: '口苦、睡眠浅一周', icon: '记', color: 'orange', iconSrc: basicInfoIcons.chief_complaint },
    { key: 'recent_status', label: '近期情况', value: '近期辛辣偏多；睡眠浅，易醒', icon: '食', color: 'green', iconSrc: basicInfoIcons.recent_status }
  ],
  summaryText: '主诉口苦、睡眠浅一周，舌象见淡红舌、白苔、薄苔、腻苔/腐腻倾向，提示湿浊或痰湿偏重倾向。',
  tongueAnnotations: {
    root: { label: '舌苔', value: '白苔' },
    side: { label: '苔厚', value: '薄苔' },
    body: { label: '舌体', value: '淡红舌' },
    tip: { label: '特点', value: '腐腻倾向' }
  },
  tongueResults: [
    { key: 'quality', label: '舌质', value: '淡红舌', percent: 50, color: 'blue' },
    { key: 'coating', label: '舌苔', value: '白苔、薄苔', percent: 45, color: 'blue' },
    { key: 'feature', label: '特点', value: '腻苔/腐腻倾向', percent: 60, color: 'blue' }
  ],
  simpleExplanation: '多为相对平和舌色，仍需结合舌苔和主诉综合判断；白薄苔伴腐腻倾向，提示湿浊偏重。',
  possibleProblem: '湿浊或痰湿偏重倾向，伴肝胆气机受影响',
  evidenceList: [
    '淡红舌提示基础状态尚可。',
    '口苦提示肝胆相关不适。',
    '腻苔提示湿浊偏重。'
  ],
  currentStatusText: '当前属于中等但可控，需结合主诉和舌象变化继续观察。',
  riskText: '中等但可控',
  riskSubText: '需继续观察症状变化',
  riskScore: 55,
  coreProblemSummary: '饮食偏辛辣可能助湿生热，影响肝胆气机和睡眠。',
  problemChain: [
    { label: '饮食因素' },
    { label: '湿浊内生' },
    { label: '影响气机' },
    { label: '干扰睡眠' }
  ],
  attentionText: '若症状持续、加重或出现警示信号，应及时线下就医评估。',
  avoidFoods: [
    { name: '辛辣', icon: '辣' },
    { name: '辣椒', icon: '辣' },
    { name: '酒', icon: '杯' },
    { name: '油炸', icon: '油' },
    { name: '肥肉', icon: '肉' },
    { name: '奶茶', icon: '茶' }
  ],
  recommendFoods: [
    { name: '冬瓜', icon: '瓜' },
    { name: '赤小豆', icon: '豆' },
    { name: '芹菜', icon: '菜' }
  ],
  dietTip: '晚餐应在18:30前完成且不宜过饱',
  lifestyle: [
    '每日固定22:30前上床',
    '晨起后户外缓行15–20分钟',
    '避免熬夜，尤其23:00–3:00',
    '记录每日口苦发生时段和强度'
  ]
};

function compactText(items) {
  return uniqueTexts(items.map(cleanText)).join('；') || '暂无';
}

function cleanText(value) {
  return String(value || '')
    .replace(/。；/g, '；')
    .replace(/；。/g, '；')
    .replace(/；{2,}/g, '；')
    .replace(/。{2,}/g, '。')
    .trim();
}

function clampNumber(value, fallback, min = 0, max = 100) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, number));
}

function normalizeGender(value) {
  return genderTextMap[value] || value || '未知';
}

function uniqueTexts(items) {
  return [...new Set(items.filter(Boolean))];
}

function normalizeTongueReport(data) {
  const patient = data.patient || data.request || {};
  const report = data.report || {};
  const imageRender = data.image_analysis?.image_analysis_render || report.image_analysis?.image_analysis_render || {};
  const risk = report.risk_assessment_render || {};
  const diet = report.diet_advice_render || {};
  const lifestyle = report.lifestyle_advice_render || {};
  const observation = report.tongue_observation_render || {};
  const problem = report.tcm_pattern_analysis_render?.possible_problem || {};
  const patternItems = report.pattern_differentiation_render?.items || [];
  const evidenceItems = report.evidence_chain_render?.items || [];
  const problemChain = report.symptom_correlation_render?.problem_chain || [];
  const basicItems = patient.basic_info_render?.items || [];
  const basicItemMap = basicItems.reduce((result, item) => {
    result[item.key] = item;
    return result;
  }, {});
  const chiefComplaint = basicItemMap.chief_complaint?.value || patient.chief_complaint || data.chief_complaint || '暂无';
  const recentStatus = basicItemMap.recent_status?.value || compactText([
    patient.diet && `饮食：${patient.diet}`,
    patient.sleep && `睡眠：${patient.sleep}`,
    patient.duration && `持续：${patient.duration}`,
    report.chief_complaint_correlation_render?.items?.[0]?.relation
  ]);
  const annotationByKey = (imageRender.annotation_items || []).reduce((result, item) => {
    result[item.key] = { label: item.label, value: item.value };
    return result;
  }, {});
  const annotationByPosition = (imageRender.annotation_items || []).reduce((result, item) => {
    result[item.position_hint] = { label: item.label, value: item.value };
    return result;
  }, {});
  const possibleProblem = problem.content && problem.content !== '证候倾向'
    ? problem.content
    : uniqueTexts(patternItems.map(item => item.conclusion)).join('、') || fallbackReport.possibleProblem;

  return {
    basicInfo: [
      { key: 'gender', label: '性别', value: normalizeGender(patient.gender), icon: '性', color: 'blue', iconSrc: basicInfoIcons.gender },
      { key: 'age', label: '年龄', value: patient.age ? `${patient.age}岁` : '未知', icon: '龄', color: 'blue', iconSrc: basicInfoIcons.age },
      { key: 'chief_complaint', label: '主诉', value: chiefComplaint, icon: '记', color: 'orange', iconSrc: basicInfoIcons.chief_complaint },
      { key: 'recent_status', label: '近期情况', value: recentStatus, icon: '食', color: 'green', iconSrc: basicInfoIcons.recent_status }
    ],
    summaryText: report.summary_render?.short_content || data.summary_render?.short_content || data.summary || fallbackReport.summaryText,
    tongueAnnotations: {
      root: annotationByKey.tongue_coating || annotationByPosition.left_top || fallbackReport.tongueAnnotations.root,
      side: annotationByKey.coating_thickness || annotationByPosition.left_middle || fallbackReport.tongueAnnotations.side,
      body: annotationByKey.tongue_body || annotationByPosition.right_middle || fallbackReport.tongueAnnotations.body,
      tip: annotationByKey.tongue_feature || annotationByPosition.right_bottom || fallbackReport.tongueAnnotations.tip
    },
    tongueResults: (imageRender.result_bars || fallbackReport.tongueResults).map(item => ({
      key: item.key || item.label,
      label: item.label,
      value: item.value,
      percent: clampNumber(item.percent, 0),
      color: item.color_key || item.color || 'blue'
    })),
    simpleExplanation: cleanText(observation.simple_explanation?.content) || fallbackReport.simpleExplanation,
    possibleProblem,
    evidenceList: evidenceItems.length ? uniqueTexts(evidenceItems.map(item => cleanText(item.content))) : fallbackReport.evidenceList,
    currentStatusText: risk.current_status?.content || fallbackReport.currentStatusText,
    riskText: risk.risk_text || fallbackReport.riskText,
    riskSubText: risk.current_status?.severity_text ? `${risk.current_status.severity_text}风险，需继续观察` : fallbackReport.riskSubText,
    riskScore: clampNumber(risk.risk_score, fallbackReport.riskScore),
    coreProblemSummary: risk.core_problem_summary || fallbackReport.coreProblemSummary,
    problemChain: problemChain.length ? problemChain.map(item => ({ label: item.label })) : fallbackReport.problemChain,
    attentionText: risk.attention?.content || fallbackReport.attentionText,
    avoidFoods: (diet.avoid?.length ? diet.avoid : fallbackReport.avoidFoods).map(item => ({
      name: item.name,
      icon: item.icon_key || item.icon
    })),
    recommendFoods: (diet.recommend?.length ? diet.recommend : fallbackReport.recommendFoods).map(item => ({
      name: item.name,
      icon: item.icon_key || item.icon
    })),
    dietTip: diet.tips?.length ? compactText(diet.tips.map(item => item.content)) : fallbackReport.dietTip,
    lifestyle: lifestyle.items?.length
      ? lifestyle.items.map(item => {
        const title = cleanText(item.title);
        const content = cleanText(item.content);
        return title && content && title !== content ? `${title}：${content}` : content || title;
      }).filter(Boolean)
      : fallbackReport.lifestyle
  };
}

export default {
  name: 'TongueReport',
  components: {
    SectionTitle
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      titleIcons,
      ...fallbackReport
    };
  },
  computed: {
    riskMarkerStyle() {
      const left = Math.max(6, Math.min(94, this.riskScore));
      return { left: `${left}%` };
    },
    riskNeedleStyle() {
      const degree = Math.max(-58, Math.min(58, (this.riskScore - 50) * 1.16));
      return { transform: `rotate(${degree}deg)` };
    }
  },
  mounted() {
    this.fetchTongueReport();
  },
  methods: {
    getReportId() {
      return this.$route.query.order_id || this.$route.query.report_id || this.$route.query.local_report_id || this.$route.query.id || DEFAULT_REPORT_ID;
    },
    async fetchTongueReport() {
      const reportId = this.getReportId();
      if (!reportId) {
        this.loadError = '缺少报告ID，当前展示示例数据';
        return;
      }
      if (!TONGUE_API_KEY) {
        this.loadError = '缺少舌诊接口密钥，当前展示示例数据';
        return;
      }

      this.isLoading = true;
      this.loadError = '';

      try {
        const headers = new Headers();
        headers.append('Accept', 'application/json');
        headers.append('X-Gateway-API-Key', TONGUE_API_KEY);
        headers.append('X-API-Key', TONGUE_ORDER_API_KEY);

        const response = await fetch(`${TONGUE_API_BASE}/api/api/orders/${encodeURIComponent(reportId)}/report`, {
          method: 'GET',
          headers
        });
        const result = await response.json();
        const isSuccess = result.errorCode === 0 || result.code === 0;
        if (!isSuccess || !result.data) {
          throw new Error(result.message || '舌诊报告加载失败');
        }
        Object.assign(this, normalizeTongueReport(result.data));
      } catch (error) {
        this.loadError = `${error.message || '舌诊报告加载失败'}，当前展示示例数据`;
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tongue-report-page {
  min-height: 100vh;
  color: #333333;
  background: #f5f6f9;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

.phone-shell {
  min-height: 100vh;
  max-width: 750px;
  margin: 0 auto;
  overflow: hidden;
  background: #f5f6f9;
}

.page-body {
  padding: 12px 30px 56px;
}

.page-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(245, 246, 249, 0.86);
  backdrop-filter: blur(8px);
}

.loading-spinner {
  width: 58px;
  height: 58px;
  border: 6px solid #d9e9ff;
  border-top-color: #2262b1;
  border-radius: 50%;
  animation: tongue-loading-spin 0.9s linear infinite;
}

.loading-text {
  margin: 18px 0 0;
  color: #2262b1;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 1px;
}

@keyframes tongue-loading-spin {
  to { transform: rotate(360deg); }
}

.state-card {
  margin: 0 0 24px;
  padding: 22px 26px;
  border: 1px solid #d9e9ff;
  border-radius: 20px;
  color: #495880;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.45;
  background: #edf4ff;
  box-shadow: none;
}

.error-card {
  border-color: #ffe2c8;
  color: #b85a1f;
  background: #fff7ef;
}

.report-section {
  margin-bottom: 54px;
}

.report-section + .report-section {
  padding-top: 4px;
}

.last-section {
  padding-bottom: 18px;
}

::v-deep .section-title {
  display: flex;
  align-items: center;
  margin: 8px 0 16px;

  h2 {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    color: #1a1a1a;
  }
}

::v-deep .section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-right: 12px;
  border-radius: 50%;
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  background: #2262b1;
  box-shadow: none;
}

.card {
  position: relative;
  overflow: hidden;
  border: 1px solid #e6edf7;
  border-radius: 20px;
  background: #fff;
  box-shadow: none;
}

.tinted-card {
  border-color: #d9e9ff;
  background: #edf4ff;
}

.info-card {
  margin-bottom: 22px;
}

.info-row {
  display: grid;
  grid-template-columns: 48px 152px 1fr;
  align-items: center;
  min-height: 76px;
  padding: 0 26px;
  border-bottom: 1px solid #e6edf7;

  &:last-child {
    border-bottom: 0;
  }
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: #fff;
  font-size: 19px;
  font-weight: 800;

  &.blue { background: #2262b1; }
  &.orange { background: #fd9243; }
  &.green { background: #3ba272; }

  &.has-image {
    background: transparent;
  }

  img {
    display: block;
    width: 38px;
    height: 38px;
    object-fit: contain;
  }
}

.info-label {
  color: #333333;
  font-size: 16px;
  font-weight: 700;
}

.info-value {
  min-width: 0;
  color: #495880;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  text-align: right;
  word-break: break-word;
}

.summary-card,
.simple-card,
.attention-card,
.advice-card,
.lifestyle-card {
  padding: 26px 28px;
}

.summary-card {
  margin-bottom: 22px;
}

.card-title {
  display: flex;
  align-items: center;
  margin-bottom: 13px;
  font-size: 25px;
  font-weight: 800;
}

.title-icon {
  display: block;
  width: 32px;
  height: 32px;
  margin-right: 11px;
  object-fit: contain;
}

.purple-title,
.blue-title,
.orange-title,
.green-title,
.red-title,
.warning-title {
  color: #18243f;
}

p {
  margin: 0;
  color: #495880;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.55;
}

.summary-card p,
.simple-card p,
.problem-card p,
.status-card p,
.attention-card p {
  font-size: 16px;
  line-height: 1.55;
}

.title-dot,
.question-dot,
.check-dot,
.warning-dot,
.shield-dot,
.bowl-dot,
.moon-dot,
.alert-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 11px;
  border-radius: 50%;
  color: #fff;
  font-size: 19px;
  font-weight: 900;
  background: #2262b1;
}

.bulb-dot::before { content: '✦'; }
.star-dot::before { content: '★'; }
.list-dot::before { content: '≡'; }
.check-dot::before { content: ''; }
.shield-dot::before { content: '盾'; font-size: 16px; }
.bowl-dot::before { content: '碗'; font-size: 16px; }
.moon-dot::before { content: '◕'; }
.alert-dot::before { content: ''; }
.question-dot { background: #fd9243; }
.warning-dot,
.alert-dot { background: #ff5c4d; }

.tongue-card {
  padding: 28px 28px 26px;
}

.tongue-map {
  position: relative;
  height: 430px;
}

.leader-lines {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;

  path {
    fill: none;
    stroke: #9eb8dc;
    stroke-width: 1.4;
    stroke-linecap: round;
  }
}

.tongue-figure {
  position: absolute;
  left: 50%;
  top: 24px;
  z-index: 1;
  width: 248px;
  height: 352px;
  transform: translateX(-50%);
}

.tongue-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  transform: scaleX(1.14) scaleY(1.02);
  transform-origin: center;
}

.tongue-base {
  fill: url(#tongueBodyGradient);
  filter: none;
}

.tongue-highlight {
  fill: url(#tongueHighlight);
}

.tongue-inner {
  fill: none;
  stroke: rgba(255, 255, 255, 0.62);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 5 6;
}

.tongue-midline {
  fill: none;
  stroke: rgba(174, 67, 73, 0.22);
  stroke-width: 7;
  stroke-linecap: round;
}

.tongue-texture {
  fill: none;
  stroke: rgba(255, 255, 255, 0.23);
  stroke-width: 3;
  stroke-linecap: round;
}

.tongue-texture.right {
  stroke: rgba(174, 67, 73, 0.12);
}
.point {
  position: absolute;
  z-index: 3;
  width: 12px;
  height: 12px;
  border: 5px solid #edf4ff;
  border-radius: 50%;
  background: #2262b1;
  box-shadow: none;
}

.point-root { left: 80px; top: 96px; }
.point-side { left: 54px; top: 218px; }
.point-body { right: 54px; top: 210px; }
.point-tip { left: 118px; bottom: 48px; }

.tongue-label {
  position: absolute;
  z-index: 3;
  min-width: 118px;
  color: #959fb7;
  font-size: 23px;
  line-height: 1.35;

  span,
  strong {
    display: block;
  }

  strong {
    color: #fd9243;
    font-size: 24px;
    font-weight: 800;
  }

  .red-text {
    color: #ff5c4d;
  }
}

.label-root { left: 4px; top: 98px; }
.label-side { left: 4px; top: 226px; }
.label-body { right: 0; top: 172px; }
.label-tip { right: 0; top: 316px; }
.right { text-align: left; }

.result-list {
  padding-top: 12px;
  border-top: 1px solid #e6edf7;
}

.result-row {
  display: grid;
  grid-template-columns: 232px 1fr;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.result-meta {
  display: flex;
  align-items: center;
  color: #333333;
  font-size: 24px;
  font-weight: 800;
}

.progress-track {
  height: 9px;
  overflow: hidden;
  border-radius: 999px;
  background: #e0edff;

  i {
    display: block;
    height: 100%;
    border-radius: inherit;
  }

  .red { background: #ff5c4d; }
  .blue { background: #2262b1; }
}

.simple-card {
  margin-top: 22px;
}

.problem-card,
.evidence-card,
.status-card,
.severity-card,
.advice-card,
.lifestyle-card {
  padding: 26px 28px;
  margin-bottom: 22px;
}

.evidence-card {
  display: grid;
  grid-template-columns: 1fr 154px;
  align-items: center;
  gap: 18px;
}

.severity-card {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  align-items: stretch;
  gap: 18px;
}

.evidence-card {
  align-items: stretch;
}

.evidence-row {
  margin-top: 12px;

  p {
    font-size: 16px;
    line-height: 1.45;
  }
}

.clipboard-mark {
  width: 118px;
  height: 138px;
  margin: auto;
  border: 9px solid #d9e9ff;
  border-radius: 14px;
  opacity: 0.82;

  &::before {
    content: '';
    display: block;
    width: 50px;
    height: 20px;
    margin: -18px auto 18px;
    border-radius: 14px;
    background: #d9e9ff;
  }

  &::after {
    content: '✓\A✓\A✓';
    white-space: pre;
    display: block;
    color: #b5c7f7;
    font-size: 28px;
    font-weight: 900;
    line-height: 1.45;
    text-align: center;
  }
}

.status-scale {
  position: relative;
  height: 9px;
  margin: 28px 8px 14px;
  border-radius: 999px;
  background: linear-gradient(90deg, #3ba272 0 34%, #fd9243 34% 68%, #ff5c4d 68% 100%);

  span {
    position: absolute;
    left: 38%;
    top: 50%;
    width: 22px;
    height: 22px;
    border: 5px solid #ffffff;
    border-radius: 50%;
    background: #2262b1;
    box-shadow: none;
    transform: translate(-50%, -50%);
  }
}

.scale-text {
  display: flex;
  justify-content: space-between;
  padding: 0 70px;

  b {
    color: #495880;
    font-size: 22px;
    font-weight: 800;
  }
}

.risk-panel {
  padding-right: 14px;
  border-right: 1px solid #e6edf7;
}

.gauge-wrap {
  position: relative;
  width: 168px;
  height: 96px;
  margin: 24px auto 0;
}

.gauge-arc {
  width: 168px;
  height: 84px;
  overflow: hidden;
  border-radius: 168px 168px 0 0;
  background: conic-gradient(from 270deg at 50% 100%, #2262b1 0 58deg, #fd9243 58deg 88deg, #dfe4ec 88deg 180deg);

  &::after {
    content: '';
    position: absolute;
    left: 32px;
    right: 32px;
    bottom: 10px;
    height: 58px;
    border-radius: 120px 120px 0 0;
    background: #ffffff;
  }
}

.gauge-needle {
  position: absolute;
  left: 50%;
  bottom: 12px;
  width: 5px;
  height: 66px;
  border-radius: 8px;
  background: #1a1a1a;
  transform: rotate(32deg);
  transform-origin: bottom;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -8px;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #1a1a1a;
    transform: translateX(-50%);
  }
}

.risk-panel strong {
  display: block;
  margin-top: 12px;
  color: #2262b1;
  font-size: 28px;
  font-weight: 900;
  text-align: center;
}

.risk-panel > span {
  display: block;
  margin-top: 8px;
  color: #959fb7;
  font-size: 21px;
  text-align: center;
}

.core-panel {
  padding-left: 8px;

  p {
    margin-bottom: 20px;
    font-size: 16px;
  }
}

.chain-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.chain-item {
  position: relative;
  flex: 1;
  min-width: 0;
  text-align: center;

  b {
    display: block;
    min-height: 36px;
    color: #495880;
    font-size: 17px;
    font-weight: 800;
    line-height: 1.35;
  }

  i {
    position: absolute;
    right: -8px;
    top: 12px;
    width: 16px;
    height: 2px;
    background: #b5c7f7;
  }
}

.attention-card {
  border-color: #ffe2c8;
  background: #fff7ef;
}

.diet-tag-group {
  margin-top: 4px;
}

.diet-tag-group + .diet-tag-group {
  margin-top: 22px;
}

.advice-label {
  margin: 0 0 14px;
  color: #495880;
  font-size: 23px;
  font-weight: 800;
}

.diet-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 16px;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 84px;
    height: 46px;
    padding: 0 18px;
    border-radius: 999px;
    font-size: 22px;
    font-weight: 800;
    background: #ffffff;
  }
}

.diet-tag-group.avoid .diet-tags span {
  color: #ff5c4d;
  border: 2px solid #ffd1cb;
  background: #fff7f6;
}

.diet-tag-group.recommend .diet-tags span {
  color: #3ba272;
  border: 2px solid #cce9db;
  background: #f7fffb;
}


.meal-time {
  display: flex;
  align-items: center;
  margin-top: 22px;
  padding: 16px 0 0;
  border-top: 1px solid #e6edf7;
  color: #495880;
  font-size: 16px;
  font-weight: 800;

  span {
    margin-right: 12px;
    color: #959fb7;
    font-size: 30px;
  }
}

.lifestyle-card {
  margin-top: 18px;
}

.life-row {
  margin-top: 16px;

  p {
    font-size: 16px;
    line-height: 1.45;
  }
}

@media (min-width: 751px) {
  .phone-shell {
    max-width: 670px;
    margin: 40px auto;
    border-radius: 34px;
  }
}

@media (max-width: 480px) {
  .page-body {
    padding: 12px 22px 46px;
  }

  .info-row {
    grid-template-columns: 42px 112px 1fr;
    min-height: 72px;
    padding: 0 20px;
  }

  .info-label,
  .info-value,
  p {
    font-size: 14px;
  }

  .summary-card p,
  .simple-card p,
  .problem-card p,
  .status-card p,
  .attention-card p,
  .evidence-row p,
  .core-panel p,
  .life-row p,
  .meal-time {
    font-size: 14px;
    line-height: 1.5;
  }

  .result-row,
  .severity-card,
  .evidence-card {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .risk-panel {
    padding-right: 0;
    padding-bottom: 22px;
    border-right: 0;
    border-bottom: 1px solid #e6edf7;
  }

  .core-panel {
    padding-left: 0;
  }

  .clipboard-mark {
    display: none;
  }

  .chain-row {
    gap: 10px;
  }

  .chain-item i {
    right: -12px;
  }
}

@media (max-width: 360px) {
  .page-body {
    padding-left: 22px;
    padding-right: 22px;
  }

  .result-row {
    grid-template-columns: 1fr;
  }

  .tongue-card,
  .problem-card,
  .evidence-card,
  .status-card,
  .severity-card,
  .advice-card,
  .lifestyle-card {
    padding-left: 22px;
    padding-right: 22px;
  }
}
</style>
